import { useEffect, useRef, useState } from 'react';
import { fetchPauseEverything, pauseEverything, type PauseEverythingView } from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { formatStatusDate } from '../lib/format-date';
import { InfoTip } from '../lib/info-tip';
import { labels } from '../lib/labels';
import { confirmResumeEverything } from '../lib/resume-everything';
import { RoleNote, useGate } from '../lib/session';

/** The DOM id of the First pass strip: the card's button scrolls to it and focuses it. */
export const FIRST_PASS_ID = 'first-pass-movement';

/** QA-128: the documents a pause still lets finish, and those that wait, from the First pass strip. */
export interface PauseCounts {
  finishing: number;
  finishingIsFloor: boolean;
  waiting: number;
  waitingIsFloor: boolean;
}

/** Scroll the strip into view and move focus to it. Not a `#` link: the hash is the route. */
function goToFirstPass() {
  const strip = document.getElementById(FIRST_PASS_ID);
  if (!strip) return;
  strip.scrollIntoView({ block: 'start' });
  strip.focus({ preventScroll: true });
}

/**
 * The Pipeline status card (Figma `2158:13995`, Finding 9): a heading, one sentence on what pausing
 * does, the status line, and one button at the end of the action bar. Running, the button is the
 * filled red *Pause pipeline* (ADR 0018, QA-108); paused, it is *Resume pipeline*, through the
 * Resume flow in `lib/resume-everything.ts`. QA-117: the card is the only place `#processing` shows
 * the pause. The page does not draw the `everything-paused` work-list row.
 *
 * `paused` and `detail` come from the processing view (the `everything-paused` row), so the card and
 * the page always agree, and a slow or failed read never shows *Running* while the pipeline is
 * paused. The card reads `GET /api/processing/pause-everything` only for who paused it and when:
 * once each time the pipeline turns paused, and again when `detail` changes (the operator and
 * unreadable causes have different detail text), not on every poll tick. Its own Pause write
 * answers with the same view, so that answer fills the line at once. Until the details are in, the
 * line is *Paused* with no date or name. A pause flag that could not be read has no date or name:
 * the line says so. If the read fails, the row's `detail` shows under the line, so that warning
 * never depends on the read.
 *
 * QA-128: while paused, and when the processing view carries the First pass strip (`counts`), a
 * line under the status says how many documents still finish and how many wait. The counts come
 * from the strip, which the page polls, not from the pause read, which would freeze them.
 *
 * **Guarded**: the confirm dialog says what pausing does before acting, and the parent reloads
 * `#processing` to pick up the new state.
 */
export function PauseEverythingControl({ paused, detail, counts, onPaused }: { paused: boolean; detail?: string; counts?: PauseCounts; onPaused: () => void }) {
  const [details, setDetails] = useState<PauseEverythingView | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  // The `detail` the details were read under. A change while paused clears them before the new read.
  const readFor = useRef<string | undefined>(undefined);
  const action = useActionError();
  const gate = useGate('administer');

  useEffect(() => {
    if (!paused) {
      readFor.current = undefined;
      setDetails(null);
      setReadFailed(false);
      return undefined;
    }
    // Else a failed read after a change to `unreadable` keeps the old date and name.
    if (readFor.current !== undefined && readFor.current !== detail) setDetails(null);
    readFor.current = detail;
    setReadFailed(false);
    let cancelled = false;
    fetchPauseEverything()
      .then((result) => {
        if (!cancelled && result.paused) setDetails(result);
      })
      .catch(() => {
        if (!cancelled) setReadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [paused, detail]);

  async function handlePause() {
    // One dialog, not three: the confirmation and the optional reason were a
    // `window.confirm` followed by a `window.prompt`, each of which the operator could
    // cancel halfway through a write they had already agreed to.
    const answers = await ask({
      ...labels.pauseEverything.pauseConfirm,
      fields: [{ name: 'reason', label: labels.pauseEverything.reasonLabel }],
      confirmLabel: labels.pauseEverything.pauseConfirmButton,
      tone: 'danger',
    });
    if (!answers) return;
    let result: PauseEverythingView | undefined;
    if (await action.run(async () => (result = await pauseEverything(answers.reason || undefined)))) {
      if (result?.paused) setDetails(result);
      onPaused();
    }
  }

  async function handleResume() {
    if (await confirmResumeEverything(action.run)) onPaused();
  }

  const status = !paused
    ? labels.pauseEverything.statusRunning
    : details?.cause === 'unreadable'
      ? labels.pauseEverything.statusUnreadable
      : labels.pauseEverything.statusPaused(details?.pausedAt ? formatStatusDate(details.pausedAt) : null, details?.pausedBy ?? null);

  return (
    <section className="pipeline-status" aria-labelledby="pipeline-status-heading">
      <div className="pipeline-status__content">
        <h2 id="pipeline-status-heading" className="pipeline-status__heading">
          {labels.pauseEverything.cardHeading}
        </h2>
        <p className="pipeline-status__body">
          {labels.pauseEverything.cardBody}
          <InfoTip label={labels.pauseEverything.cardTipLabel} text={labels.pauseEverything.cardTip} />
        </p>
      </div>
      <div className="pipeline-status__actions">
        <div className="pipeline-status__text">
          <p className="pipeline-status__status">{status}</p>
          <RoleNote action="administer" />
          {paused && readFailed && detail && <p className="pipeline-status__status">{detail}</p>}
          {paused && counts && (
            <p className="pipeline-status__status">
              {labels.pauseEverything.counts(counts.finishing, counts.finishingIsFloor, counts.waiting, counts.waitingIsFloor)}{' '}
              <button type="button" className="btn btn--text" onClick={goToFirstPass}>
                {labels.pauseEverything.seeMovement}
              </button>
            </p>
          )}
        </div>
        {paused ? (
          <button type="button" className="btn pipeline-status__btn" onClick={handleResume} {...gate.props}>
            {labels.pauseEverything.resumeButton}
          </button>
        ) : (
          <button type="button" className="btn btn--danger-fill pipeline-status__btn" onClick={handlePause} {...gate.props}>
            {labels.pauseEverything.pauseButton}
          </button>
        )}
      </div>
      {action.notice}
    </section>
  );
}
